import React, { useState, useEffect } from 'react'
import { Hotel, ShoppingCart, X, Calendar, Leaf, BarChart3, ArrowLeft, User, CreditCard, Info, Edit2, CheckCircle, AlertTriangle, CalendarClock } from 'lucide-react'
import { FaCcVisa, FaCcMastercard, FaCcAmex, FaCcDiscover, FaCheck, FaTimes } from 'react-icons/fa'
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js'
import experienceCheckoutService from '../services/experienceCheckoutService'
import { getCartItemPaymentType } from '../utils/paymentPolicyUtils'
import { getHotelCartLinePayable } from '../utils/agentCommissionCart'
import { getHotelStaySourcePresentation } from '../utils/staySourceLabel'
import HotelCartItemPricing from './checkout/HotelCartItemPricing'

// Minimalistic Full Screen Loader Component with Progress Tracking
const FullScreenLoader = ({ isLoading, message = "Processing your payment..." }) => {
  const [progress, setProgress] = useState(0)
  const [currentStep, setCurrentStep] = useState(0)

  const steps = [
    "Initializing payment",
    "Verifying card details",
    "Processing transaction",
    "Securing reservation",
    "Generating confirmation"
  ]

  useEffect(() => {
    if (!isLoading) {
      setProgress(0)
      setCurrentStep(0)
      return
    }

    // Reset progress when loading starts
    setProgress(0)
    setCurrentStep(0)

    const progressInterval = setInterval(() => {
      setProgress(prev => {
        const newProgress = prev + 25 // Increment by 25% each step
        return newProgress > 100 ? 100 : newProgress
      })
    }, 600) // Adjust timing as needed

    const stepInterval = setInterval(() => {
      setCurrentStep(prev => {
        const nextStep = prev + 1
        return nextStep >= steps.length ? steps.length - 1 : nextStep
      })
    }, 1200)

    return () => {
      clearInterval(progressInterval)
      clearInterval(stepInterval)
    }
  }, [isLoading])

  if (!isLoading) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/75 backdrop-blur-sm transition-all duration-300">
      <div className="text-center max-w-sm mx-4">
        {/* Animated checkmark circle */}
        <div className="relative w-24 h-24 mx-auto mb-8">
          {/* Outer circle */}
          <div className="absolute inset-0 border-2 border-slate-200 rounded-full"></div>

          {/* Progress circle that fills based on progress */}
          <div
            className="absolute inset-0 border-2 border-transparent border-t-green-600 rounded-full animate-spin"
            style={{
              borderTopColor: progress >= 20 ? '#16a34a' : 'transparent',
              borderRightColor: progress >= 40 ? '#16a34a' : 'transparent',
              borderBottomColor: progress >= 60 ? '#16a34a' : 'transparent',
              borderLeftColor: progress >= 80 ? '#16a34a' : 'transparent',
            }}
          ></div>

          {/* Center checkmark - appears when complete */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-16 h-16 bg-green-50 border border-green-100 rounded-full flex items-center justify-center">
              {progress === 100 ? (
                <CheckCircle className="w-8 h-8 text-green-600 animate-pulse" />
              ) : (
                <div className="w-6 h-6 border-2 border-green-600 border-t-transparent rounded-full animate-spin"></div>
              )}
            </div>
          </div>
        </div>

        {/* Title */}
        <h3 className="text-2xl font-semibold text-slate-900 mb-3">
          {progress === 100 ? 'Almost Done!' : 'Finalizing Your Booking'}
        </h3>

        {/* Progress percentage */}
        <div className="mb-4">
          <span className="text-3xl font-bold text-green-600">{progress}%</span>
          <span className="text-sm text-slate-500 ml-2">complete</span>
        </div>

        {/* Message */}
        <p className="text-slate-600 mb-8 leading-relaxed">
          {progress === 100
            ? 'Finishing up...'
            : steps[currentStep] || message}
        </p>

        {/* Progress bar */}
        <div className="w-full bg-slate-100 rounded-full h-2 mb-8 overflow-hidden">
          <div
            className="bg-gradient-to-r from-green-500 to-green-600 h-2 rounded-full transition-all duration-500 ease-out"
            style={{ width: `${progress}%` }}
          ></div>
        </div>

        {/* Progress indicators */}
        <div className="relative h-8 mb-8">
          {steps.map((step, index) => (
            <div
              key={index}
              className={`absolute top-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium transition-all duration-300 ${index <= currentStep
                ? 'bg-green-600 text-white scale-110 shadow-lg'
                : 'bg-slate-200 text-slate-400'
                }`}
              style={{
                left: `${(index / (steps.length - 1)) * 100}%`,
                transform: `translateX(-50%) ${index <= currentStep ? 'scale(110%)' : ''}`
              }}
            >
              {index + 1}
            </div>
          ))}
          {/* Connecting line */}
          <div className="absolute top-3 left-0 right-0 h-0.5 bg-slate-200 -z-10">
            <div
              className="h-full bg-green-600 transition-all duration-500 ease-out"
              style={{ width: `${(currentStep / (steps.length - 1)) * 100}%` }}
            ></div>
          </div>
        </div>

        {/* Step descriptions */}
        <div className="grid grid-cols-2 gap-3 text-xs mb-8">
          {steps.map((step, index) => (
            <div
              key={index}
              className={`p-2 rounded-lg transition-all duration-300 ${index === currentStep
                ? 'bg-green-50 border border-green-100'
                : 'text-slate-500'
                } ${index <= currentStep ? 'font-medium' : ''}`}
            >
              <div className="flex items-center">
                <div className={`w-2 h-2 rounded-full mr-2 ${index === currentStep
                  ? 'bg-green-600 animate-pulse'
                  : index < currentStep
                    ? 'bg-green-400'
                    : 'bg-slate-300'
                  }`}></div>
                <span className={index === currentStep ? 'text-green-700' : ''}>
                  {step}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Note */}
        <div className="mt-8 pt-6 border-t border-slate-200">
          <p className="text-xs text-slate-500">
            {progress === 100
              ? 'Your booking will be confirmed momentarily...'
              : 'This usually takes 10-15 seconds. Please don\'t refresh the page.'}
          </p>
        </div>
      </div>
    </div>
  );
};

// Alternative: Simple progress bar only loader (Uncomment to use)
/*
const FullScreenLoader = ({ isLoading, message = "Processing your payment..." }) => {
  const [progress, setProgress] = useState(0)

  useEffect(() => {
    if (!isLoading) {
      setProgress(0)
      return
    }

    const interval = setInterval(() => {
      setProgress(prev => {
        const increment = Math.random() * 10 + 5 // Random increment between 5-15%
        const newProgress = prev + increment
        return newProgress >= 95 ? 95 : newProgress // Stop at 95%, complete at end
      })
    }, 300)

    return () => clearInterval(interval)
  }, [isLoading])

  useEffect(() => {
    if (progress >= 95 && isLoading) {
      const timeout = setTimeout(() => setProgress(100), 500)
      return () => clearTimeout(timeout)
    }
  }, [progress, isLoading])

  if (!isLoading) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/95 backdrop-blur-sm transition-all duration-300">
      <div className="text-center max-w-md w-full mx-4">
        <div className="mb-8">
          <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
          <h3 className="text-2xl font-semibold text-slate-900 mb-2">
            Processing Payment
          </h3>
          <p className="text-slate-600">{message}</p>
        </div>

        <div className="space-y-4">
          <div className="flex justify-between items-center text-sm">
            <span className="text-slate-700">Progress</span>
            <span className="font-semibold text-green-600">{Math.round(progress)}%</span>
          </div>
          
          <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden">
            <div 
              className="bg-green-600 h-2 rounded-full transition-all duration-300 ease-out"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
          
          <div className="text-xs text-slate-500 pt-4 border-t border-slate-100">
            <p>Please wait while we secure your booking</p>
          </div>
        </div>
      </div>
    </div>
  );
};
*/

const PaymentStep = ({
  profile,
  selectedCustomerForItem,
  userPaymentDetails,
  selectedPaymentMethod,
  useNewCard,
  newCardDetails,
  billingAddress,
  summary,
  isSaving,
  onPreviousStep,
  onPaymentMethodSelect,
  onUseNewCardChange,
  onNewCardChange,
  onBillingAddressChange,
  onCardNumberChange,
  onExpiryChange,
  getFormattedExpiry,
  expiryError,
  onCompleteCheckout,
  onNavigateHotels,
  paymentFlowByItemId = {},
  onDeleteSavedPaymentMethod,
  getHotelLinePayableDisplay = getHotelCartLinePayable,
  /** Optional QA promo block rendered in the checkout side panel (hotel cart). */
  qaPromoApplySection = null
}) => {
  const stripe = useStripe()
  const elements = useElements()
  const [savingStripeCard, setSavingStripeCard] = useState(false)
  const [stripeCardError, setStripeCardError] = useState('')
  const [stripeCardComplete, setStripeCardComplete] = useState(false)
  const [saveCardForLater, setSaveCardForLater] = useState(true)

  const hasScheduledItems = Array.isArray(profile)
    ? profile.some((item) => {
        if (item?.type === 'experience') return false
        return paymentFlowByItemId[String(item?.id)] === 'scheduled'
      })
    : false

  const cardBrandIcons = {
    visa: <FaCcVisa className="h-6 w-6" />,
    mastercard: <FaCcMastercard className="h-6 w-6" />,
    amex: <FaCcAmex className="h-6 w-6" />,
    discover: <FaCcDiscover className="h-6 w-6" />,
    default: <CreditCard className="h-6 w-6" />,
  }

  // Local helper functions for card formatting
  const detectCardType = (cardNumber) => {
    const cleaned = cardNumber.replace(/\s+/g, '')

    // Visa
    if (/^4[0-9]{12}(?:[0-9]{3})?$/.test(cleaned)) {
      return 'visa'
    }
    // Mastercard
    if (/^5[1-5][0-9]{14}$/.test(cleaned)) {
      return 'mastercard'
    }
    // American Express
    if (/^3[47][0-9]{13}$/.test(cleaned)) {
      return 'amex'
    }
    // Discover
    if (/^6(?:011|5[0-9]{2})[0-9]{12}$/.test(cleaned)) {
      return 'discover'
    }

    return ''
  }

  // Local card number change handler
  const handleCardNumberChangeLocal = (value) => {
    const cleaned = value.replace(/\s+/g, '')
    const chunks = cleaned.match(/.{1,4}/g)
    const formatted = chunks ? chunks.join(' ') : ''
    const cardType = detectCardType(value)

    onNewCardChange({
      ...newCardDetails,
      cardNumber: formatted,
      cardType: cardType
    })
  }

  // Local expiry change handler
  const handleExpiryChangeLocal = (value) => {
    const cleaned = value.replace(/\D/g, '')

    if (cleaned.length <= 2) {
      onNewCardChange({
        ...newCardDetails,
        expiryMonth: cleaned,
        expiryYear: ''
      })
    } else {
      onNewCardChange({
        ...newCardDetails,
        expiryMonth: cleaned.slice(0, 2),
        expiryYear: cleaned.slice(2, 4)
      })
    }
  }

  // If parent provides these functions, use them; otherwise use local versions
  const handleCardNumberChange = onCardNumberChange || handleCardNumberChangeLocal
  const handleExpiryChange = onExpiryChange || handleExpiryChangeLocal
  const formattedExpiry = getFormattedExpiry ? getFormattedExpiry() :
    `${newCardDetails.expiryMonth}${newCardDetails.expiryYear ? '/' + newCardDetails.expiryYear : ''}`

  const normalizeSavedStripeMethod = (pm = {}) => {
    const id = pm._id || pm.id || pm.cardId || pm.stripePaymentMethodId
    return {
      ...pm,
      _id: pm._id || id,
      id: pm.id || id,
      stripePaymentMethodId: pm.stripePaymentMethodId || pm.cardId || null,
      stripeCustomerId: pm.stripeCustomerId || null,
      brand: pm.brand || pm.cardType || 'card',
      last4: pm.last4 || '****',
      expMonth: pm.expMonth || pm.expiryMonth || '',
      expYear: pm.expYear || pm.expiryYear || '',
      funding: pm.funding || pm.cardType || 'card'
    }
  }

  const handleCheckoutClick = async () => {
    if (!useNewCard) {
      if (selectedPaymentMethod?.walletType) {
        setStripeCardError('CardElement-only flow currently supports saved reusable card methods only.')
        return
      }
      onCompleteCheckout()
      return
    }

    if (!newCardDetails?.cardholderName?.trim()) {
      setStripeCardError('Cardholder name is required.')
      return
    }
    if (!billingAddress?.street || !billingAddress?.city || !billingAddress?.stateProvince || !billingAddress?.postalCode || !billingAddress?.countryCode) {
      setStripeCardError('Please fill all billing address fields.')
      return
    }
    if (!stripe || !elements) {
      setStripeCardError('Stripe is still initializing. Please wait a moment.')
      return
    }
    if (!stripeCardComplete) {
      setStripeCardError('Please complete your card details.')
      return
    }

    const cardElement = elements.getElement(CardElement)
    if (!cardElement) {
      setStripeCardError('Card input is not ready yet.')
      return
    }

    setSavingStripeCard(true)
    setStripeCardError('')
    try {
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: {
          name: newCardDetails.cardholderName,
          address: {
            line1: billingAddress.street,
            city: billingAddress.city,
            state: billingAddress.stateProvince,
            postal_code: billingAddress.postalCode,
            country: billingAddress.countryCode
          }
        }
      })

      if (error || !paymentMethod?.id) {
        throw new Error(error?.message || 'Failed to tokenize card with Stripe.')
      }

      if (hasScheduledItems || saveCardForLater) {
        // We need a reusable saved card for scheduled payments,
        // and we also allow optionally saving the card for later use.
        // (If you uncheck, we currently still require saving for hotel checkout to proceed.)
      } else {
        throw new Error('Saving the card is required for hotel checkout.')
      }

      const saved = await experienceCheckoutService.saveStripePaymentMethod(paymentMethod.id)
      const savedPm = normalizeSavedStripeMethod(saved?.paymentMethod || {})

      onPaymentMethodSelect(savedPm)
      onUseNewCardChange(false)
      onCompleteCheckout(savedPm)
    } catch (error) {
      setStripeCardError(error.message || 'Failed to save Stripe card.')
    } finally {
      setSavingStripeCard(false)
    }
  }

  const countryCodes = [
    { code: 'AF', name: 'Afghanistan' },
    { code: 'AL', name: 'Albania' },
    { code: 'DZ', name: 'Algeria' },
    { code: 'AS', name: 'American Samoa' },
    { code: 'AD', name: 'Andorra' },
    { code: 'AO', name: 'Angola' },
    { code: 'AI', name: 'Anguilla' },
    { code: 'AQ', name: 'Antarctica' },
    { code: 'AG', name: 'Antigua and Barbuda' },
    { code: 'AR', name: 'Argentina' },
    { code: 'AM', name: 'Armenia' },
    { code: 'AW', name: 'Aruba' },
    { code: 'AU', name: 'Australia' },
    { code: 'AT', name: 'Austria' },
    { code: 'AZ', name: 'Azerbaijan' },
    { code: 'BS', name: 'Bahamas' },
    { code: 'BH', name: 'Bahrain' },
    { code: 'BD', name: 'Bangladesh' },
    { code: 'BB', name: 'Barbados' },
    { code: 'BY', name: 'Belarus' },
    { code: 'BE', name: 'Belgium' },
    { code: 'BZ', name: 'Belize' },
    { code: 'BJ', name: 'Benin' },
    { code: 'BM', name: 'Bermuda' },
    { code: 'BT', name: 'Bhutan' },
    { code: 'BO', name: 'Bolivia' },
    { code: 'BA', name: 'Bosnia and Herzegovina' },
    { code: 'BW', name: 'Botswana' },
    { code: 'BV', name: 'Bouvet Island' },
    { code: 'BR', name: 'Brazil' },
    { code: 'IO', name: 'British Indian Ocean Territory' },
    { code: 'BN', name: 'Brunei Darussalam' },
    { code: 'BG', name: 'Bulgaria' },
    { code: 'BF', name: 'Burkina Faso' },
    { code: 'BI', name: 'Burundi' },
    { code: 'KH', name: 'Cambodia' },
    { code: 'CM', name: 'Cameroon' },
    { code: 'CA', name: 'Canada' },
    { code: 'CV', name: 'Cape Verde' },
    { code: 'KY', name: 'Cayman Islands' },
    { code: 'CF', name: 'Central African Republic' },
    { code: 'TD', name: 'Chad' },
    { code: 'CL', name: 'Chile' },
    { code: 'CN', name: 'China' },
    { code: 'CX', name: 'Christmas Island' },
    { code: 'CC', name: 'Cocos (Keeling) Islands' },
    { code: 'CO', name: 'Colombia' },
    { code: 'KM', name: 'Comoros' },
    { code: 'CG', name: 'Congo' },
    { code: 'CD', name: 'Congo, The Democratic Republic of the' },
    { code: 'CK', name: 'Cook Islands' },
    { code: 'CR', name: 'Costa Rica' },
    { code: 'CI', name: 'Cote D\'Ivoire' },
    { code: 'HR', name: 'Croatia' },
    { code: 'CU', name: 'Cuba' },
    { code: 'CY', name: 'Cyprus' },
    { code: 'CZ', name: 'Czech Republic' },
    { code: 'DK', name: 'Denmark' },
    { code: 'DJ', name: 'Djibouti' },
    { code: 'DM', name: 'Dominica' },
    { code: 'DO', name: 'Dominican Republic' },
    { code: 'EC', name: 'Ecuador' },
    { code: 'EG', name: 'Egypt' },
    { code: 'SV', name: 'El Salvador' },
    { code: 'GQ', name: 'Equatorial Guinea' },
    { code: 'ER', name: 'Eritrea' },
    { code: 'EE', name: 'Estonia' },
    { code: 'ET', name: 'Ethiopia' },
    { code: 'FK', name: 'Falkland Islands (Malvinas)' },
    { code: 'FO', name: 'Faroe Islands' },
    { code: 'FJ', name: 'Fiji' },
    { code: 'FI', name: 'Finland' },
    { code: 'FR', name: 'France' },
    { code: 'GF', name: 'French Guiana' },
    { code: 'PF', name: 'French Polynesia' },
    { code: 'TF', name: 'French Southern Territories' },
    { code: 'GA', name: 'Gabon' },
    { code: 'GM', name: 'Gambia' },
    { code: 'GE', name: 'Georgia' },
    { code: 'DE', name: 'Germany' },
    { code: 'GH', name: 'Ghana' },
    { code: 'GI', name: 'Gibraltar' },
    { code: 'GR', name: 'Greece' },
    { code: 'GL', name: 'Greenland' },
    { code: 'GD', name: 'Grenada' },
    { code: 'GP', name: 'Guadeloupe' },
    { code: 'GU', name: 'Guam' },
    { code: 'GT', name: 'Guatemala' },
    { code: 'GG', name: 'Guernsey' },
    { code: 'GN', name: 'Guinea' },
    { code: 'GW', name: 'Guinea-Bissau' },
    { code: 'GY', name: 'Guyana' },
    { code: 'HT', name: 'Haiti' },
    { code: 'HM', name: 'Heard Island and Mcdonald Islands' },
    { code: 'VA', name: 'Holy See (Vatican City State)' },
    { code: 'HN', name: 'Honduras' },
    { code: 'HK', name: 'Hong Kong' },
    { code: 'HU', name: 'Hungary' },
    { code: 'IS', name: 'Iceland' },
    { code: 'IN', name: 'India' },
    { code: 'ID', name: 'Indonesia' },
    { code: 'IR', name: 'Iran, Islamic Republic Of' },
    { code: 'IQ', name: 'Iraq' },
    { code: 'IE', name: 'Ireland' },
    { code: 'IM', name: 'Isle of Man' },
    { code: 'IL', name: 'Israel' },
    { code: 'IT', name: 'Italy' },
    { code: 'JM', name: 'Jamaica' },
    { code: 'JP', name: 'Japan' },
    { code: 'JE', name: 'Jersey' },
    { code: 'JO', name: 'Jordan' },
    { code: 'KZ', name: 'Kazakhstan' },
    { code: 'KE', name: 'Kenya' },
    { code: 'KI', name: 'Kiribati' },
    { code: 'KP', name: 'Korea, Democratic People\'s Republic of' },
    { code: 'KR', name: 'Korea, Republic of' },
    { code: 'KW', name: 'Kuwait' },
    { code: 'KG', name: 'Kyrgyzstan' },
    { code: 'LA', name: 'Lao People\'s Democratic Republic' },
    { code: 'LV', name: 'Latvia' },
    { code: 'LB', name: 'Lebanon' },
    { code: 'LS', name: 'Lesotho' },
    { code: 'LR', name: 'Liberia' },
    { code: 'LY', name: 'Libyan Arab Jamahiriya' },
    { code: 'LI', name: 'Liechtenstein' },
    { code: 'LT', name: 'Lithuania' },
    { code: 'LU', name: 'Luxembourg' },
    { code: 'MO', name: 'Macao' },
    { code: 'MK', name: 'Macedonia, The Former Yugoslav Republic of' },
    { code: 'MG', name: 'Madagascar' },
    { code: 'MW', name: 'Malawi' },
    { code: 'MY', name: 'Malaysia' },
    { code: 'MV', name: 'Maldives' },
    { code: 'ML', name: 'Mali' },
    { code: 'MT', name: 'Malta' },
    { code: 'MH', name: 'Marshall Islands' },
    { code: 'MQ', name: 'Martinique' },
    { code: 'MR', name: 'Mauritania' },
    { code: 'MU', name: 'Mauritius' },
    { code: 'YT', name: 'Mayotte' },
    { code: 'MX', name: 'Mexico' },
    { code: 'FM', name: 'Micronesia, Federated States of' },
    { code: 'MD', name: 'Moldova, Republic of' },
    { code: 'MC', name: 'Monaco' },
    { code: 'MN', name: 'Mongolia' },
    { code: 'ME', name: 'Montenegro' },
    { code: 'MS', name: 'Montserrat' },
    { code: 'MA', name: 'Morocco' },
    { code: 'MZ', name: 'Mozambique' },
    { code: 'MM', name: 'Myanmar' },
    { code: 'NA', name: 'Namibia' },
    { code: 'NR', name: 'Nauru' },
    { code: 'NP', name: 'Nepal' },
    { code: 'NL', name: 'Netherlands' },
    { code: 'AN', name: 'Netherlands Antilles' },
    { code: 'NC', name: 'New Caledonia' },
    { code: 'NZ', name: 'New Zealand' },
    { code: 'NI', name: 'Nicaragua' },
    { code: 'NE', name: 'Niger' },
    { code: 'NG', name: 'Nigeria' },
    { code: 'NU', name: 'Niue' },
    { code: 'NF', name: 'Norfolk Island' },
    { code: 'MP', name: 'Northern Mariana Islands' },
    { code: 'NO', name: 'Norway' },
    { code: 'OM', name: 'Oman' },
    { code: 'PK', name: 'Pakistan' },
    { code: 'PW', name: 'Palau' },
    { code: 'PS', name: 'Palestinian Territory, Occupied' },
    { code: 'PA', name: 'Panama' },
    { code: 'PG', name: 'Papua New Guinea' },
    { code: 'PY', name: 'Paraguay' },
    { code: 'PE', name: 'Peru' },
    { code: 'PH', name: 'Philippines' },
    { code: 'PN', name: 'Pitcairn' },
    { code: 'PL', name: 'Poland' },
    { code: 'PT', name: 'Portugal' },
    { code: 'PR', name: 'Puerto Rico' },
    { code: 'QA', name: 'Qatar' },
    { code: 'RE', name: 'Reunion' },
    { code: 'RO', name: 'Romania' },
    { code: 'RU', name: 'Russian Federation' },
    { code: 'RW', name: 'Rwanda' },
    { code: 'SH', name: 'Saint Helena' },
    { code: 'KN', name: 'Saint Kitts and Nevis' },
    { code: 'LC', name: 'Saint Lucia' },
    { code: 'MF', name: 'Saint Martin' },
    { code: 'PM', name: 'Saint Pierre and Miquelon' },
    { code: 'VC', name: 'Saint Vincent and the Grenadines' },
    { code: 'WS', name: 'Samoa' },
    { code: 'SM', name: 'San Marino' },
    { code: 'ST', name: 'Sao Tome and Principe' },
    { code: 'SA', name: 'Saudi Arabia' },
    { code: 'SN', name: 'Senegal' },
    { code: 'RS', name: 'Serbia' },
    { code: 'SC', name: 'Seychelles' },
    { code: 'SL', name: 'Sierra Leone' },
    { code: 'SG', name: 'Singapore' },
    { code: 'SK', name: 'Slovakia' },
    { code: 'SI', name: 'Slovenia' },
    { code: 'SB', name: 'Solomon Islands' },
    { code: 'SO', name: 'Somalia' },
    { code: 'ZA', name: 'South Africa' },
    { code: 'GS', name: 'South Georgia and the South Sandwich Islands' },
    { code: 'ES', name: 'Spain' },
    { code: 'LK', name: 'Sri Lanka' },
    { code: 'SD', name: 'Sudan' },
    { code: 'SR', name: 'Suriname' },
    { code: 'SJ', name: 'Svalbard and Jan Mayen' },
    { code: 'SZ', name: 'Swaziland' },
    { code: 'SE', name: 'Sweden' },
    { code: 'CH', name: 'Switzerland' },
    { code: 'SY', name: 'Syrian Arab Republic' },
    { code: 'TW', name: 'Taiwan' },
    { code: 'TJ', name: 'Tajikistan' },
    { code: 'TZ', name: 'Tanzania, United Republic of' },
    { code: 'TH', name: 'Thailand' },
    { code: 'TL', name: 'Timor-Leste' },
    { code: 'TG', name: 'Togo' },
    { code: 'TK', name: 'Tokelau' },
    { code: 'TO', name: 'Tonga' },
    { code: 'TT', name: 'Trinidad and Tobago' },
    { code: 'TN', name: 'Tunisia' },
    { code: 'TR', name: 'Turkey' },
    { code: 'TM', name: 'Turkmenistan' },
    { code: 'TC', name: 'Turks and Caicos Islands' },
    { code: 'TV', name: 'Tuvalu' },
    { code: 'UG', name: 'Uganda' },
    { code: 'UA', name: 'Ukraine' },
    { code: 'AE', name: 'United Arab Emirates' },
    { code: 'GB', name: 'United Kingdom' },
    { code: 'US', name: 'United States' },
    { code: 'UM', name: 'United States Minor Outlying Islands' },
    { code: 'UY', name: 'Uruguay' },
    { code: 'UZ', name: 'Uzbekistan' },
    { code: 'VU', name: 'Vanuatu' },
    { code: 'VE', name: 'Venezuela' },
    { code: 'VN', name: 'Viet Nam' },
    { code: 'VG', name: 'Virgin Islands, British' },
    { code: 'VI', name: 'Virgin Islands, U.S.' },
    { code: 'WF', name: 'Wallis and Futuna' },
    { code: 'EH', name: 'Western Sahara' },
    { code: 'YE', name: 'Yemen' },
    { code: 'ZM', name: 'Zambia' },
    { code: 'ZW', name: 'Zimbabwe' }
  ]

  return (
    <>
      <FullScreenLoader isLoading={isSaving} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Cart Items with Client Display (Read-only) */}
        <div className="lg:col-span-2">
          <div className="bg-white border border-slate-200 rounded-3xl p-4 sm:p-6 shadow-xl shadow-slate-100/50">
            <div className="flex items-start sm:items-center justify-between gap-3 mb-6 sm:mb-8 pb-4 border-b border-slate-100">
              <h2 className="text-xl sm:text-2xl typography-section-title font-black text-slate-900 tracking-tight leading-tight">
                Your Selection <span className="text-slate-400 font-medium ml-1 sm:ml-2">({profile?.length || 0})</span>
              </h2>
              <div className="px-2.5 sm:px-3 py-1 bg-slate-50 rounded-full border border-slate-100 shrink-0">
                <p className="text-[9px] sm:text-[10px] font-bold text-slate-500 uppercase tracking-[0.12em] sm:tracking-widest">Review Itinerary</p>
              </div>
            </div>

            {profile && profile.length > 0 ? (
              <div className="space-y-6">
                {profile.map((item) => (
                  (() => {
                    const paymentMode = paymentFlowByItemId[String(item.id)]
                    const isImmediateStripe = paymentMode === 'immediate'
                    const paymentBadgeClasses = isImmediateStripe
                      ? 'bg-rose-50 border-rose-200 text-rose-700'
                      : 'bg-emerald-50 border-emerald-200 text-emerald-700'

                    const paymentBadgeText = isImmediateStripe
                      ? 'Immediate Payment'
                      : paymentMode === 'scheduled'
                        ? 'Scheduled Payment'
                        : 'Payment Flow Resolving...'

                    const staySrc = getHotelStaySourcePresentation(item)

                    return (
                  <div
                    key={item.id}
                    className="border border-slate-200 rounded-2xl bg-white shadow-sm overflow-hidden transition-all duration-300 hover:shadow-md hover:border-slate-300"
                  >
                    <div className="flex flex-col lg:flex-row">
                      <div className="w-full lg:w-56 h-40 lg:h-auto overflow-hidden relative">
                        <img
                          src={item.image}
                          alt={item.title}
                          className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 via-transparent to-transparent opacity-60" />
                      </div>

                      <div className="flex-1 p-5 flex flex-col">
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <h3 className="text-xl typography-card-title font-black text-slate-900 leading-tight">
                              {item.title}
                            </h3>
                            <p className="mt-1 text-[10px] font-bold uppercase tracking-wide text-slate-500">
                              {staySrc.pillText}
                              {staySrc.detailText ? (
                                <span className="ml-1.5 font-semibold normal-case text-slate-500">· {staySrc.detailText}</span>
                              ) : null}
                            </p>
                            <div className={`mt-2 inline-flex items-center gap-2 px-2.5 py-1 rounded-lg border text-[10px] font-bold uppercase tracking-wider ${paymentBadgeClasses}`}>
                              {paymentBadgeText}
                            </div>
                          </div>
                        </div>

                        {/* Client Display (Read-only) */}
                        <div className="mb-5 p-3.5 bg-slate-50 border border-slate-100 rounded-xl flex items-center justify-between transition-all duration-300">
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center border border-slate-100 shadow-sm">
                              <User className="h-4 w-4 text-slate-900" />
                            </div>
                            <div>
                              <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-0.5">Assigned Guest</p>
                              <p className="text-sm typography-body-sm font-black text-slate-900">
                                {selectedCustomerForItem[item.id]?.firstName} {selectedCustomerForItem[item.id]?.lastName}
                              </p>
                            </div>
                          </div>
                          <button
                            onClick={onPreviousStep}
                            className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-slate-400 hover:text-slate-900 hover:bg-white border border-transparent hover:border-slate-200 rounded-lg transition-all duration-200"
                          >
                            Edit
                          </button>
                        </div>

                        {/* Item Details */}
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5 pb-5 border-b border-slate-100">
                          <div className="space-y-0.5">
                            <span className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">Accommodation</span>
                            <p className="text-xs font-bold text-slate-700 truncate">{item.bedType?.type || "N/A"}</p>
                          </div>
                          <div className="space-y-0.5">
                            <span className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">Occupancy</span>
                            <p className="text-xs font-bold text-slate-700">{item.maxAdults || 0} Adults, {item.maxChildren || 0} Children</p>
                          </div>
                          <div className="space-y-0.5">
                            <span className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">Duration</span>
                            <p className="text-xs font-bold text-slate-700">{item.nights} Phenomenal Nights</p>
                          </div>
                          <div className="space-y-0.5">
                            <span className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">Rate Plan</span>
                            <p className="text-xs font-bold text-slate-700 truncate">{item.ratePlanName}</p>
                          </div>
                        </div>

                        <div className="flex flex-wrap items-center gap-4 mb-4">
                          {item.environmentalImpact != null && (
                            <div className="flex items-center gap-2 px-2.5 py-1.5 bg-emerald-50 rounded-lg border border-emerald-100">
                              <Leaf className="h-3 w-3 text-emerald-600" />
                              <span className="text-[10px] font-bold text-emerald-800 uppercase tracking-tight">
                                {parseFloat(item.environmentalImpact?.carbonEmission ?? 0).toFixed(2)} Kg CO2
                              </span>
                            </div>
                          )}
                          {item.rateDetails?.cancellationPolicy?.CancelPenalty?.[0] && (
                          <div className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg border ${item.rateDetails.cancellationPolicy.CancelPenalty[0].Refundable
                            ? 'bg-blue-50 border-blue-100 text-blue-800'
                            : 'bg-amber-50 border-amber-100 text-amber-800'
                            }`}>
                            {item.rateDetails.cancellationPolicy.CancelPenalty[0].Refundable ? <CheckCircle className="h-3 w-3" /> : <AlertTriangle className="h-3 w-3" />}
                            <span className="text-[10px] font-bold uppercase tracking-tight">
                              {item.rateDetails.cancellationPolicy.CancelPenalty[0].Refundable ? 'Refundable Stay' : 'Dynamic Policy'}
                            </span>
                          </div>
                          )}
                        </div>

                        <div className="flex items-center justify-between mt-auto pt-4 border-t border-slate-50">
                          <div className="flex items-center text-[10px] text-slate-400 font-bold uppercase tracking-widest">
                            <Calendar className="h-3 w-3 mr-2 text-slate-300" />
                            {item.checkInDate} — {item.checkOutDate}
                          </div>
                          <HotelCartItemPricing
                            item={item}
                            getPayable={getHotelLinePayableDisplay}
                            showTaxes
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                    )
                  })()
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <ShoppingCart className="h-16 w-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">Your cart is empty</h3>
                <p className="text-slate-500 mb-6">Add some hotels to get started</p>
                <button
                  onClick={onNavigateHotels}
                  className="inline-flex items-center px-6 py-3 text-white font-medium bg-slate-900 hover:bg-slate-800 rounded-lg transition-colors duration-200"
                >
                  <Hotel className="h-5 w-5 mr-2" />
                  Browse Hotels
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Payment Section */}
        <div className="lg:col-span-1">
          <div className="sticky top-10">
            <div className="bg-white border border-slate-200 rounded-3xl shadow-xl shadow-slate-100/50 p-6 overflow-hidden relative">
              <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-slate-900 to-slate-700" />
              <h2 className="text-2xl typography-section-title font-black text-slate-900 mb-6 tracking-tight">Secure Payment</h2>

              {qaPromoApplySection ? (
                <div className="mb-6">{qaPromoApplySection}</div>
              ) : null}

              {/* Saved Payment Methods */}
              <div className="mb-6">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-4">Saved Credentials</p>
                {userPaymentDetails && userPaymentDetails.length > 0 ? (
                  <div className="space-y-3">
                    {userPaymentDetails.map((payment) => {
                      // 1. Get the reliable ID for the current item
                      const paymentId = payment._id || payment.id;
                      const isWalletMethod = !!payment.walletType
                      const isDisabledCardElementOnly = isWalletMethod

                      // 2. Get the reliable ID for the currently selected item
                      const selectedId = selectedPaymentMethod?._id || selectedPaymentMethod?.id;

                      // 3. Strict comparison:
                      // - Must NOT be in "New Card" mode
                      // - Both IDs must exist (not undefined)
                      // - IDs must match
                      const isSelected = !useNewCard && paymentId && selectedId && (paymentId === selectedId);

                      return (
                        <div
                          key={paymentId}
                          onClick={() => {
                            if (isDisabledCardElementOnly) return
                            onUseNewCardChange(false);
                            onPaymentMethodSelect(payment);
                          }}
                          className={`relative border-2 rounded-2xl p-4 transition-all duration-300 mb-3 group ${isDisabledCardElementOnly ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'} ${isSelected
                            ? 'border-slate-900 bg-slate-900 text-white shadow-xl shadow-slate-200 -translate-y-0.5'
                            : 'border-slate-50 bg-slate-50 hover:border-slate-200 hover:bg-white'
                            }`}
                          title={isDisabledCardElementOnly ? 'CardElement-only flow currently supports saved reusable card methods only.' : undefined}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <div className={`w-10 h-7 flex items-center justify-center rounded-lg border transition-colors ${isSelected ? 'bg-white/10 border-white/20' : 'bg-white border-slate-100'}`}>
                                {cardBrandIcons[payment.brand?.toLowerCase()] || cardBrandIcons.default}
                              </div>
                              <div>
                                <p className={`text-sm typography-body-sm font-bold ${isSelected ? 'text-white' : 'text-slate-900'}`}>
                                  •••• •••• •••• {payment.last4}
                                </p>
                                <p className={`text-[10px] font-bold uppercase tracking-wider ${isSelected ? 'text-white/60' : 'text-slate-400'}`}>
                                  Exp. {payment.expMonth}/{payment.expYear} • {payment.funding || 'debit'}
                                </p>
                                {isWalletMethod && (
                                  <p className="text-[10px] font-semibold tracking-wider text-amber-600 mt-0.5">
                                    Wallet ({payment.walletType})
                                  </p>
                                )}
                              </div>
                            </div>

                            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all duration-300 ${isSelected ? 'border-white bg-white' : 'border-slate-200 group-hover:border-slate-300'
                              }`}>
                              {isSelected && <div className="w-2.5 h-2.5 rounded-full bg-slate-900 transition-all duration-300" />}
                            </div>

                            {typeof onDeleteSavedPaymentMethod === 'function' && (
                              <button
                                type="button"
                                onClick={(event) => {
                                  event.stopPropagation()
                                  onDeleteSavedPaymentMethod(payment)
                                }}
                                className={`ml-2 rounded-md p-1.5 transition-colors ${
                                  isSelected
                                    ? 'text-white/70 hover:bg-white/10 hover:text-white'
                                    : 'text-slate-400 hover:bg-slate-200 hover:text-slate-700'
                                }`}
                                title="Delete saved card"
                              >
                                <X className="h-3.5 w-3.5" />
                              </button>
                            )}

                            <input
                              type="radio"
                              name="paymentMethod"
                              checked={isSelected}
                              readOnly
                              className="hidden"
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-slate-500 text-sm">No saved payment methods found.</p>
                )}
              </div>

              {/* New Card Option */}
              <div className="pt-6 border-t border-slate-100">
                <div
                  className={`flex items-center p-4 rounded-2xl border-2 transition-all duration-300 cursor-pointer group mb-6 ${useNewCard ? 'border-slate-900 bg-slate-900 text-white shadow-xl shadow-slate-200 -translate-y-0.5' : 'border-slate-50 bg-slate-50 hover:border-slate-200 hover:bg-white'}`}
                  onClick={() => onUseNewCardChange(true)}
                >
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mr-4 transition-all duration-300 ${useNewCard ? 'border-white bg-white' : 'border-slate-200 group-hover:border-slate-300'}`}>
                    {useNewCard && <div className="w-2.5 h-2.5 rounded-full bg-slate-900" />}
                  </div>
                  <label className="text-[10px] font-bold uppercase tracking-widest cursor-pointer">
                    Use New Card
                  </label>
                </div>

                {useNewCard && (
                  <div className="space-y-5 mt-6 animate-in fade-in slide-in-from-top-4 duration-500">
                    <div className="flex items-start gap-3 rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3">
                      <input
                        id="save-card-for-later"
                        type="checkbox"
                        checked={saveCardForLater || hasScheduledItems}
                        disabled={hasScheduledItems}
                        onChange={(e) => setSaveCardForLater(e.target.checked)}
                        className="mt-0.5 h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900"
                      />
                      <label htmlFor="save-card-for-later" className="text-xs text-slate-700 leading-relaxed select-none">
                        <span className="font-semibold text-slate-900">Save card for later</span>
                        <span className="text-slate-500"> (recommended)</span>
                        {hasScheduledItems && (
                          <span className="block text-[11px] text-slate-500 mt-1">
                            Required because your cart includes scheduled payments.
                          </span>
                        )}
                      </label>
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2 ml-1">
                        Cardholder Name
                      </label>
                      <input
                        type="text"
                        value={newCardDetails.cardholderName}
                        onChange={(e) => onNewCardChange({ ...newCardDetails, cardholderName: e.target.value })}
                        className="w-full px-4 py-3.5 bg-slate-50 border border-slate-100 rounded-xl focus:bg-white focus:ring-2 focus:ring-slate-900/5 focus:border-slate-900 transition-all duration-300 text-sm font-medium"
                        placeholder="John Doe"
                      />
                    </div>

                    <div>
                      <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2 ml-1">
                        Card Details
                      </label>
                      <div className="rounded-xl border border-slate-200 bg-white p-4">
                        <CardElement
                          options={{
                            hidePostalCode: true,
                            style: {
                              base: {
                                fontSize: '14px',
                                color: '#0f172a',
                                '::placeholder': { color: '#94a3b8' }
                              },
                              invalid: { color: '#dc2626' }
                            }
                          }}
                          onChange={(event) => {
                            setStripeCardComplete(!!event.complete)
                            if (event.error) {
                              setStripeCardError(event.error.message || 'Invalid card details')
                            } else {
                              setStripeCardError('')
                            }
                          }}
                        />
                      </div>
                      {!!stripeCardError && (
                        <p className="text-[11px] font-medium text-red-600 mt-2">{stripeCardError}</p>
                      )}
                    </div>

                    {/* Billing Address Section */}
                    <div className="pt-6 border-t border-slate-100">
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-4">Billing Address</p>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">
                            Street Address
                          </label>
                          <input
                            type="text"
                            value={billingAddress.street}
                            onChange={(e) => onBillingAddressChange({ ...billingAddress, street: e.target.value })}
                            className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500 transition-colors"
                            placeholder="Enter street address"
                          />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-2">
                              City
                            </label>
                            <input
                              type="text"
                              value={billingAddress.city}
                              onChange={(e) => onBillingAddressChange({ ...billingAddress, city: e.target.value })}
                              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500 transition-colors"
                              placeholder="Enter city"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-2">
                              State/Province
                            </label>
                            <input
                              type="text"
                              value={billingAddress.stateProvince}
                              onChange={(e) => onBillingAddressChange({ ...billingAddress, stateProvince: e.target.value })}
                              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500 transition-colors"
                              placeholder="Enter state/province"
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-2">
                              Postal Code
                            </label>
                            <input
                              type="text"
                              value={billingAddress.postalCode}
                              onChange={(e) => onBillingAddressChange({ ...billingAddress, postalCode: e.target.value })}
                              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500 transition-colors"
                              placeholder="Enter postal code"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-2">
                              Country
                            </label>
                            <select
                              value={billingAddress.countryCode}
                              onChange={(e) => onBillingAddressChange({ ...billingAddress, countryCode: e.target.value })}
                              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500 transition-colors"
                            >
                              {countryCodes.map(country => (
                                <option key={country.code} value={country.code}>
                                  {country.name}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Order Summary */}
              {summary && (
                <div className="mt-8 p-6 bg-slate-50 rounded-2xl border border-slate-100 relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-24 h-24 bg-slate-100/50 rounded-full -mr-12 -mt-12" />
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-4">Cost Breakdown</p>
                  <div className="space-y-3 relative z-10">
                    <div className="flex justify-between items-center">
                      <span className="text-xs font-bold text-slate-500 uppercase tracking-tight">Base Amount</span>
                      <span className="text-sm font-bold text-slate-900">{summary.currency} {summary.subtotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs font-bold text-slate-500 uppercase tracking-tight">Tax and fees</span>
                      <span className="text-sm font-bold text-slate-900">
                        {summary.currency} {(Number(summary.tax || 0) + Number(summary.fees || 0)).toFixed(2)}
                      </span>
                    </div>
                    {(summary.markupTotal || 0) > 0 && (
                      <div className="flex justify-between items-center">
                        <span className="text-xs font-bold text-slate-500 uppercase tracking-tight">Markup</span>
                        <span className="text-sm font-bold text-slate-900">
                          {summary.currency} {summary.markupTotal.toFixed(2)}
                        </span>
                      </div>
                    )}
                    <div className="border-t border-slate-200 pt-3 mt-3">
                      <div className="flex justify-between items-end">
                        <span className="text-xs font-black text-slate-900 uppercase tracking-widest">Total Payable</span>
                        <div className="text-right">
                          <span className="text-2xl typography-section-title font-black text-slate-900">{summary.currency} {summary.total.toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Scheduled vs Immediate Payment Split */}
              {profile && profile.length > 0 && (() => {
                const immediateItems = []
                const scheduledItems = []
                profile.forEach((item) => {
                  if (item.type === 'experience') return
                  const pt = getCartItemPaymentType(item)
                  const staySrc = getHotelStaySourcePresentation(item)
                  // Use the same fee-inclusive payable logic as cart/checkout to avoid mismatches,
                  // especially for legacy items where totals may be stored in different shapes.
                  const amount = getHotelLinePayableDisplay(item) || 0
                  const row = { ...pt, title: item.title, amount, currency: item.currency || 'USD', stayPill: staySrc.pillText, stayDetail: staySrc.detailText }
                  if (pt.type === 'scheduled') scheduledItems.push(row)
                  else immediateItems.push(row)
                })
                if (scheduledItems.length === 0 && immediateItems.length === 0) return null
                const currency = profile[0]?.currency || 'USD'
                const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(n)
                const immediateTotal = immediateItems.reduce((s, i) => s + i.amount, 0)
                const scheduledTotal = scheduledItems.reduce((s, i) => s + i.amount, 0)

                return (
                  <div className="mt-4 rounded-2xl overflow-hidden border border-slate-200 bg-white shadow-sm">
                    <div className="relative px-5 py-3 bg-white border-b border-slate-200 overflow-hidden">
                      {/* Stronger animated wave ribbons */}
                      <div className="pointer-events-none absolute inset-y-0 -left-20 w-56 bg-gradient-to-r from-transparent via-blue-400/45 to-transparent animate-pulse" />
                      <div className="pointer-events-none absolute inset-y-0 left-1/3 w-52 bg-gradient-to-r from-transparent via-indigo-400/40 to-transparent animate-pulse [animation-delay:500ms]" />
                      <div className="pointer-events-none absolute inset-y-0 right-0 w-56 bg-gradient-to-r from-transparent via-cyan-300/35 to-transparent animate-pulse [animation-delay:950ms]" />

                      <div className="relative flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {/* <div className="h-7 w-7 rounded-lg bg-slate-900 text-white flex items-center justify-center shadow-sm ring-1 ring-white/20">
                            <CreditCard className="w-4 h-4" />
                          </div> */}
                          <p className="text-[10px] font-extrabold uppercase tracking-[0.2em] text-slate-900">
                            {scheduledItems.length > 0 ? 'Payment Schedule' : 'Payment Summary'}
                          </p>
                        </div>
                      </div>
                    </div>

                    {immediateItems.length > 0 && (
                      <div className="px-5 py-4 border-b border-slate-100">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <CreditCard className="w-4 h-4 text-emerald-600" />
                            <span className="text-[11px] font-bold text-emerald-700 uppercase tracking-wider">Charged Today</span>
                          </div>
                          <span className="text-sm font-bold text-slate-800">{fmt(immediateTotal)}</span>
                        </div>
                        <div className="space-y-1 pl-6">
                          {immediateItems.map((item, idx) => (
                            <div key={idx} className="flex justify-between text-xs text-slate-500 gap-2">
                              <div className="min-w-0">
                                <span className="block truncate max-w-[170px]">{item.title}</span>
                                <span className="block text-[9px] font-bold uppercase tracking-wide text-slate-400 mt-0.5 truncate">
                                  {item.stayPill}
                                  {item.stayDetail ? <span className="font-semibold normal-case text-slate-400"> · {item.stayDetail}</span> : null}
                                </span>
                              </div>
                              <span className="font-medium text-slate-600 ml-2 shrink-0">{fmt(item.amount)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {scheduledItems.length > 0 && (
                      <>
                        <div className="px-5 py-4 bg-gradient-to-r from-blue-950 to-slate-900">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <CalendarClock className="w-4 h-4 text-white" />
                              <span className="text-[11px] font-bold text-white uppercase tracking-wider">Scheduled Payments</span>
                            </div>
                            <span className="text-sm font-bold text-white">{fmt(scheduledTotal)}</span>
                          </div>
                          <div className="space-y-2 pl-6">
                            {scheduledItems.map((item, idx) => (
                              <div key={idx} className="flex justify-between items-start text-xs gap-2">
                                <div className="min-w-0">
                                  <span className="block text-blue-50 truncate max-w-[140px]">{item.title}</span>
                                  <span className="block text-[9px] font-bold uppercase tracking-wide text-blue-200/90 mt-0.5 truncate">
                                    {item.stayPill}
                                    {item.stayDetail ? <span className="font-semibold normal-case text-blue-100/90"> · {item.stayDetail}</span> : null}
                                  </span>
                                </div>
                                <div className="text-right shrink-0">
                                  <span className="font-medium text-white block">{fmt(item.amount)}</span>
                                  {item.scheduledDate && (
                                    <span className="text-[10px] text-blue-100 font-medium">{item.label}</span>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className="px-5 py-3 bg-gradient-to-r from-blue-950 to-slate-900 border-t border-slate-800 flex items-start gap-2.5">
                          <CalendarClock className="w-3.5 h-3.5 text-white shrink-0 mt-0.5" />
                          <p className="text-[11px] text-white leading-relaxed">
                            Scheduled payments are automatically charged to your saved card 72 hours before the free cancellation deadline. You will receive a reminder email before each charge.
                          </p>
                        </div>
                      </>
                    )}
                  </div>
                )
              })()}

              <div className="flex items-center gap-4 mt-8">
                <button
                  onClick={onPreviousStep}
                  disabled={isSaving}
                  className="flex-1 px-6 py-4 text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 hover:text-slate-900 border border-slate-100 hover:border-slate-900 rounded-2xl transition-all duration-300 disabled:opacity-50"
                >
                  Back
                </button>
                <button
                  onClick={handleCheckoutClick}
                  disabled={isSaving || savingStripeCard}
                  className="flex-[2] relative overflow-hidden px-8 py-4 bg-slate-900 hover:bg-black text-white rounded-2xl transition-all duration-500 shadow-xl shadow-slate-200 hover:shadow-slate-300 disabled:opacity-50 group"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/5 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
                  <span className="relative flex items-center justify-center text-[10px] font-bold uppercase tracking-[0.25em]">
                    {isSaving || savingStripeCard ? (
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      'Initialize Journey'
                    )}
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default PaymentStep